import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Loader2 } from "lucide-react";
import { SmsHubLayout } from "@/components/sms/SmsHubLayout";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { listReplies, listOptOuts } from "@/server/sms.functions";
import { useAuth } from "@/hooks/useAuth";

export const Route = createFileRoute("/dashboard/admin/sms/replies")({
  head: () => ({ meta: [{ title: "Replies · SMS" }, { name: "robots", content: "noindex" }] }),
  component: Page,
});

function Page() {
  const { primaryRole } = useAuth();
  const repFn = useServerFn(listReplies);
  const optFn = useServerFn(listOptOuts);

  const { data: replies, isLoading } = useQuery({ queryKey: ["sms-replies"], queryFn: () => repFn(), enabled: primaryRole === "admin", refetchInterval: 30_000 });
  const { data: optOuts } = useQuery({ queryKey: ["sms-opt-outs"], queryFn: () => optFn(), enabled: primaryRole === "admin" });

  return (
    <SmsHubLayout title="Replies & Opt-outs">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <h3 className="text-[13px] font-semibold mb-2 font-display">Inbound replies</h3>
          <div className="card-surface-flat">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>From</TableHead>
                  <TableHead>Body</TableHead>
                  <TableHead>Opt-out</TableHead>
                  <TableHead>Received</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && <TableRow><TableCell colSpan={4} className="text-center py-6"><Loader2 className="h-4 w-4 animate-spin inline" /></TableCell></TableRow>}
                {!isLoading && (replies ?? []).length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-6 text-muted-foreground">No replies yet. Live in Phase 2.</TableCell></TableRow>}
                {(replies ?? []).map((r: any) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono text-[12px]">{r.from_number}</TableCell>
                    <TableCell className="text-[12px]">{r.body}</TableCell>
                    <TableCell>{r.is_opt_out ? <span className="text-[10px] px-2 py-0.5 rounded-md bg-rose-500/15 text-rose-700">YES</span> : "—"}</TableCell>
                    <TableCell className="text-[11px] text-muted-foreground">{new Date(r.received_at).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
        <div>
          <h3 className="text-[13px] font-semibold mb-2 font-display">Opt-out list</h3>
          <div className="card-surface-flat">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Phone</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>When</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(optOuts ?? []).length === 0 && <TableRow><TableCell colSpan={3} className="text-center py-6 text-muted-foreground">None yet.</TableCell></TableRow>}
                {(optOuts ?? []).map((o: any) => (
                  <TableRow key={o.id}>
                    <TableCell className="font-mono text-[12px]">{o.phone}</TableCell>
                    <TableCell className="text-[12px]">{o.source}</TableCell>
                    <TableCell className="text-[11px] text-muted-foreground">{new Date(o.opted_out_at).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      <p className="text-[11px] text-muted-foreground mt-4">
        When a reply contains any form of <strong>NO / no / n / nope / no thanks</strong>, the number is automatically added to opt-outs and the suppression list — no further SMS will ever be sent. Optional confirmation reply: <em>"You have been opted out from Heallexa SMS notifications."</em>
      </p>
    </SmsHubLayout>
  );
}
